define([
    "dojo/_base/declare",
    "dojo/_base/event",
    "dojo/_base/lang",
    "dojo/dom",
    "dojo/dom-attr",
    "dojo/dom-class",
    "dojo/dom-construct",
    "dojo/dom-geometry",
    "dojo/dom-style",
    "dojo/fx/easing",
    "dojo/Evented",
    "dojo/mouse",
    "dojo/on",
    "dojo/touch",
    "dojo/Stateful",
    "dijit/focus",
    "dijit/popup",
    "dijit/layout/_LayoutWidget",
    "mw-form/ContextMenu",
    "mw-form/MenuItem",
    "./AnimationTiming",
    "./Geometry",
    "./LayoutChildProperty",
    "./LayoutContainerEvent",
    "./LayoutUtils",
    "./SelectionBlocker"
], function (declare, event, lang, dom, domAttr, domClass, domConstruct, domGeometry, domStyle, easing, Evented, mouse, on, touch,
             Stateful, focus, popup, _LayoutWidget, ContextMenu, MenuItem, AnimationTiming, Geometry,
             LayoutChildProperty, LayoutContainerEvent, LayoutUtils, SelectionBlocker) {
    /**
     * Properties supported via the Stateful API
     */
    var ORIENTATION = "orientation";
    var SHRINK_WIDTH_TO_FIT = "shrinkWidthToFit";
    var SHRINK_HEIGHT_TO_FIT = "shrinkHeightToFit";
    var MINIMUM_BUTTON_WIDTH = "minimumButtonWidth";
    var MINIMUM_BUTTON_HEIGHT = "minimumButtonHeight";

    var PARALLEL_DRAG_THRESHOLD   = 5;
    var SMALL_DRAG_OFF_THRESHOLD  = 6;
    var LARGE_DRAG_OFF_THRESHOLD  = 50;
    var SUCCESSIVE_MOVE_DELAY     = 100;
    var LONG_PRESS_THRESHOLD      = 500;

    /**
     * Defines a widget that displays a row or column of child "button" widgets.  It incorporates an optional
     * to let the user reorder the buttons by dragging.  It also handles overflow, i.e. situations where there are
     * more buttons than can be simultaneously displayed.
     */
    var ButtonBar = declare([_LayoutWidget, Evented], {

        orientation: "horizontal",
        reorderingEnabled: true,
        distributeButtons: false,
        shrinkWidthToFit: false,
        shrinkHeightToFit: false,
        minimumButtonWidth: 50,
        minimumButtonHeight: 0,
        setGap: 0,
        setFacet: "setId",

        displayToolTipsWhenTruncated: false,
        animationEnabled: true,
        entryExitEdge: null,
        dragOffEnabled: false,
        dragOffEdge: null,

        /* _buttonInfo: an array that the following per button.  The ordering of the array reflects the
         * display ordering of the buttons (which is not necessarily the same as the DOM element order).
         *   button
         *   properties
         *   currentBox
         *   targetBox
         *   preGap
         *   preferredSize
         *   animationInfo
         *   addPending
         *   removePending
         *   removeCallback
         *   hasToolTip
         */
        // _plusButtonInfo: holds a subset of the info above but for the special "plus button"
        /* _buttonDragInfo: object that holds the following information during a button drag operation
         *   buttonInfo - array
         *   setId  if dragging set
         *   startX
         *   startY
         *   lastX
         *   lastY
         *   startIndex
         *   currentIndex
         *   connections
         *   lowerLimit
         *   upperLimit
         *   hadOrderChanged
         */
        // _showingRange
        // _previousButton
        // _nextButton
        // _previousButtonBox
        // _nextButtonBox
        // _preNeighbor
        // _postNeighbor
        // _preNeighborBox
        // _postNeighborBox
        // _fillerWidget
        // _fillerBox

        constructor: function () {
            this._buttonInfo = [];
        },

        buildRendering: function () {
            this.inherited(arguments);
            domClass.add(this.domNode, "buttonBar");

            // Add scroll buttons
            this._previousButton = domConstruct.create("div", {id : this.id + "_previousButton"}, this.domNode);
            dom.setSelectable(this._previousButton, false);
            domStyle.set(this._previousButton, {position: "absolute"});
            LayoutUtils.preventFocus(this._previousButton, true);
            domStyle.set(this._previousButton, {display: "none"});
            on(this._previousButton, touch.press, lang.hitch(this, "_handleScrollButtonPress"));

            this._nextButton = domConstruct.create("div", {id : this.id + "_nextButton"}, this.domNode);
            dom.setSelectable(this._nextButton, false);
            domStyle.set(this._nextButton, {position: "absolute"});
            LayoutUtils.preventFocus(this._nextButton, true);
            domStyle.set(this._nextButton, {display: "none"});
            on(this._nextButton, touch.press, lang.hitch(this, "_handleScrollButtonPress"));
        },

        startup: function () {
            if (this._started) {
                return;
            }
            this.inherited(arguments);

            // Take initial scroll button measurements.
            // Scroll button measurements can be inaccurate depending on the state of this._previousButton during startup,
            // thus we use a method that will allow us to recalulate these values if needed.
            this._updateScrollButtonDimension("width");
            this._updateScrollButtonDimension("height");
            this._updateScrollButtonIcons();
        },
        
        _updateScrollButtonDimension: function (dimension) {
            var tempClass = (dimension === "height") ? "scrollUpButton" : "scrollLeftButton";
            var altClass = (dimension === "height") ? "scrollLeftButton" : "scrollUpButton";
            
            var hasTempClass = this._previousButton.classList.contains(tempClass);
            var hasAltClass = this._previousButton.classList.contains(altClass);
            
            if (hasAltClass) {
                this._previousButton.classList.remove(altClass);
            }
            if (!hasTempClass) {
                this._previousButton.classList.add(tempClass);
            }            
            
            this["_scrollButton" + ((dimension === "height") ? "Height" : "Width")] = Math.round(domStyle.get(this._previousButton, dimension));
            
            if (!hasTempClass) {
                this._previousButton.classList.remove(tempClass);
            }
            if (hasAltClass) {
                this._previousButton.classList.add(altClass);
            }
        },

        destroy: function () {
            this._abortAnimation();
            this.inherited(arguments);
        },

        /**
         * Adds a button to the bar.  If the scrollToShow parameter is true then buttons on the bar will
         * scroll as needed to make the new addition visible.
         *
         * Relevant fields of the properties object are:
         *
         *     label - when the button's label property changes it will be resized as necessary
         *     checked - when the button is checked it will be scrolled into view if necessary
         *     index - will be used to determine and updated to reflect the button order
         *     setId - if specified indicates that the button is part of a set that should be kept together.
         *                Drag operations will be restricted to ensure this and index values will be interpreted/
         *                reported relative to the start of the set.
         */
        addButton: function (button, properties, scrollToShow, index, immediate) {
            var that = this;
            var buttonInfo = {
                button: button,
                properties: properties,
                targetBox: {l:0, t:0, w:0, h:0},
                currentBox: {l:0, t:0, w:0, h:0},
                isShowing: true,
                addPending: true,
                hasToolTip: !!domAttr.get(button.domNode, "title")
            };
            var deferredAdd, referenceIndex;

            // Set child positioning to absolute
            domStyle.set(button.domNode, {position: "absolute"});
            this._bindButtonProperties(buttonInfo, properties);

            if (index === undefined) {
                index = properties.get(LayoutChildProperty.INDEX);
            }
            referenceIndex = this._indexOfFirstInSet(this._setIdFromProperties(properties));
            if (referenceIndex < 0) {
                referenceIndex = this._buttonInfo.length;
            }
            if (index === undefined || index < 0 || index > this._buttonInfo.length) {
                // If an index hasn't been specified place the button at the end of its set or at the end of the bar
                // if it is the first in its set
                index = this._indexOfLastInSet(this._setIdFromProperties(properties));
                if (index < 0) {
                    index = this._buttonInfo.length;
                } else {
                    index += 1;
                }
            } else {
                // Interpret the index relative to the start of the set to which the button belongs
                index += referenceIndex;
            }

            if (index !== undefined && index >= 0 && index < this._buttonInfo.length) {
                  this._buttonInfo.splice(index, 0, buttonInfo);
            } else {
                index = this._buttonInfo.length;
                this._buttonInfo.push(buttonInfo);
            }
            if (scrollToShow) {
                this._justAddedIndex = index;
            }
            buttonInfo.properties.set(LayoutChildProperty.INDEX, index - referenceIndex);
            if (index !== this._indexOfLastInSet(this._setIdFromProperties(properties))) {
                this._updateButtonIndices();
            }

            // Attach listener for drag
            buttonInfo.connection = on(button.domNode, touch.press, lang.hitch(this, "_beginButtonDrag", buttonInfo));

            // Defer slightly to avoid unnecessary gyrations when multiple buttons are added in succession.
            if (immediate === undefined) {
                immediate = true;
            }
            if (!this._addPending) {
                this._abortAnimation();
                this._addPending = true;
                deferredAdd = function() {
                    var i, buttonInfo, startTime;
                    that._addPending = false;
                    if (!that.domNode) {
                        return;  // Happens in tests if bar is destroyed before animation completes.
                    }
                    if (that._started && !immediate && that.animationEnabled && LayoutUtils.isAnimationEnabled() && that.entryExitEdge) {
                        // Animate the entry of new tabs
                        startTime = new Date().valueOf();
                        that._computeLayout(index);
                        for (i = 0; i < that._buttonInfo.length; i++) {
                            buttonInfo = that._buttonInfo[i];
                            if (buttonInfo.addPending) {
                                buttonInfo.addPending = false;
                                Geometry.copyBox(buttonInfo.targetBox, buttonInfo.currentBox);
                                switch (that.entryExitEdge) {
                                    case "left":
                                        buttonInfo.currentBox.l -= buttonInfo.currentBox.w;
                                        break;
                                    case "right":
                                        buttonInfo.currentBox.l += buttonInfo.currentBox.w;
                                        break;
                                    case "top":
                                        buttonInfo.currentBox.t -= buttonInfo.currentBox.h;
                                        break;
                                    case "bottom":
                                        buttonInfo.currentBox.t += buttonInfo.currentBox.h;
                                }
                                // Make the button invisible to start.  _updateButtonPosition will
                                // make it visible as it comes into view
                                domStyle.set(buttonInfo.button.domNode, {visibility : "hidden"});
                                buttonInfo.isShowing = false;
                                domGeometry.setMarginBox(buttonInfo.button.domNode, buttonInfo.currentBox);
                            }
                            that._setupAnimation(buttonInfo, startTime);
                        }
                        // Also setup animation for plus button
                        if (that._plusButtonInfo) {
                            that._setupAnimation(that._plusButtonInfo, startTime);
                        }
                        that._animationDoneResponse = function () {
                            that.layout();
                        };
                        that._startAnimation();
                    } else {
                        that.layout();
                        for (i = 0; i < that._buttonInfo.length; i++) {
                            buttonInfo = that._buttonInfo[i];
                            if (buttonInfo.addPending) {
                                buttonInfo.addPending = false;
                                domStyle.set(buttonInfo.button.domNode, {visibility : "visible"});
                            }
                        }
                    }
                };
            }

            // addChild is called after the _addPending flag is set so that the resulting layout call will be
            // short-circuited, lest buttons arrive at their final locations before the animation even begins.
            domStyle.set(button.domNode, {visibility : "hidden"});
            this.addChild(button);

            if (deferredAdd) {
                if (!immediate && this.animationEnabled && LayoutUtils.isAnimationEnabled()) {
                    setTimeout(deferredAdd, 0);
                } else {
                    deferredAdd();
                }
            }
        },

        /**
         * Removes the specified button.  Note that if animation is enabled the actual removal will not be immediate.
         * The doneCallback will be called following actual removal.  If the button should not be destroyed until this time.
         */
        removeButton: function (button, doneCallback, immediate) {
            var that = this;
            var index = this._indexFromButton(button);
            var buttonInfo = this._buttonInfo[index];
            var deferredRemove;
            if (buttonInfo.unbindButtonProperties) {
                buttonInfo.unbindButtonProperties();
            }
            buttonInfo.connection.remove();
            buttonInfo.removePending = true;
            buttonInfo.removeCallback = doneCallback;

            // Defer slightly to avoid unnecessary gyrations when multiple buttons are removed in succession.
            if (!this._removePending) {
                this._abortAnimation();
                this._removePending = true;
                deferredRemove = function() {
                    if (!that.domNode) {
                        // The button bar has been destroyed, any pending remove activity is moot.
                        return;
                    }

                    // Compose a list of the remaining info
                    var i, buttonInfo, startTime;
                    var remainingInfo = [];
                    for (i = 0; i < that._buttonInfo.length; i++) {
                        buttonInfo = that._buttonInfo[i];
                        if (!buttonInfo.removePending) {
                            remainingInfo.push(buttonInfo);
                        }
                    }
                    that._animationDoneResponse = function () {
                        // When the animation is done remove the buttons and call the callbacks
                        for (i = 0; i < that._buttonInfo.length; i++) {
                            buttonInfo = that._buttonInfo[i];
                            if (buttonInfo.removePending) {
                                that.removeChild(buttonInfo.button);
                                if (buttonInfo.removeCallback) {
                                    buttonInfo.removeCallback();
                                }
                            }
                        }
                        that._buttonInfo = remainingInfo;
                        that._removePending = false;
                        that._updateButtonIndices();
                        that.layout();
                    };

                    if (immediate === undefined) {
                        immediate = true;
                    }
                    if (that._started && !immediate && that.animationEnabled && LayoutUtils.isAnimationEnabled() && that.entryExitEdge) {
                        // Animate the removal of tabs
                        // Compute the target boxes for the remaining tabs by temporarily swapping the remaining info
                        // for the child button info
                        var saveButtonInfo = that._buttonInfo;
                        that._buttonInfo = remainingInfo;
                        that._computeLayout();  // Need to pass checked button index?
                        that._buttonInfo = saveButtonInfo;

                        // Compute the exit positions of the buttons being removed
                        startTime = new Date().valueOf();
                        for (i = 0; i < that._buttonInfo.length; i++) {
                            buttonInfo = that._buttonInfo[i];
                            if (buttonInfo.removePending) {
                                switch (that.entryExitEdge) {
                                    case "left":
                                        buttonInfo.targetBox.l -= buttonInfo.targetBox.w;
                                        break;
                                    case "right":
                                        buttonInfo.targetBox.l += buttonInfo.targetBox.w;
                                        break;
                                    case "top":
                                        buttonInfo.targetBox.t -= buttonInfo.targetBox.h;
                                        break;
                                    case "bottom":
                                        buttonInfo.targetBox.t += buttonInfo.targetBox.h;
                                }
                            }
                            that._setupAnimation(buttonInfo, startTime);
                        }
                        // Also setup animation for plus button
                        if (that._plusButtonInfo) {
                            that._setupAnimation(that._plusButtonInfo, startTime);
                        }
                        that._startAnimation();
                    } else {
                        that._animationDoneResponse();
                        delete that._animationDoneResponse;
                    }
                };
                if (!immediate && this.animationEnabled && LayoutUtils.isAnimationEnabled()) {
                    setTimeout(deferredRemove, 0);
                } else {
                    deferredRemove();
                }
            }
        },

        /**
         * Returns an array containing the buttons in their current display order.  Note that the inherited getChildren
         * method may return additional children and with regard to the buttons will not necessarily reflect the current
         * display order.
         */
        getButtons: function () {
            var buttons = [];
            var i, buttonInfo;
            for (i = 0; i < this._buttonInfo.length; i++) {
                buttonInfo = this._buttonInfo[i];
                if (!buttonInfo.removePending) {
                    buttons.push(buttonInfo.button);
                }
            }
            return buttons;
        },

        /**
         * Returns an array containing the buttons associated with the given set in their current display order
         *
         * @param setId
         */
        getButtonsInSet: function (setId) {
            var buttons = [];
            var i, buttonInfo;
            var firstIndex = this._indexOfFirstInSet(setId);
            var lastIndex  = this._indexOfLastInSet(setId);
            if (firstIndex >= 0) {
                for (i = firstIndex; i <= lastIndex; i++) {
                    buttonInfo = this._buttonInfo[i];
                    if (!buttonInfo.removePending) {
                        buttons.push(buttonInfo.button);
                    }
                }
            }
            return buttons;
        },

        /**
         * Adds a special "plus" button to the bar.  Unlike other buttons the plus button always remains visible, i.e.
         * it will never scroll out of view.  The intent is for clicking on this button to trigger the addition
         * of another bar entry.  However, this response is the responsibility of the client not the ButtonBar.
         */
        setPlusButton: function (button) {
            if (this._plusButtonInfo) {
                this.removeChild(this._plusButtonInfo.button);
                this._plusButtonInfo.button.destroyRecursive();
                delete this._plusButtonInfo;
            }
            if (button) {
                this._plusButtonInfo = {
                    button: button,
                    targetBox: {l:0, t:0, w:0, h:0},
                    currentBox: {l:0, t:0, w:0, h:0},
                    isShowing: true
                };
                domStyle.set(button.domNode, {position: "absolute"});
                button.domNode.addEventListener("focus", function() {
                    button.domNode.classList.add("tabPlusButtonFocused");
                });

                button.domNode.addEventListener("blur", function() {
                    button.domNode.classList.remove("tabPlusButtonFocused");
                });

                button.domNode.tabIndex = -1;
                this.addChild(button);
            }
            this.layout();
        },

        getPlusButton: function () {
            return this._plusButtonInfo ? this._plusButtonInfo.button : null;
        },

        setPreNeighbor: function (node) {
            if (this._preNeighbor === node) {
                return;
            }
            if (this._preNeighbor) {
                LayoutUtils.removeFromParent(this._preNeighbor);
                delete this._preNeighborBox;
                this.domNode.classList.remove("buttonBar-hasPreNeighbor");
            }
            this._preNeighbor = node;
            if (node) {
                node.style.position = "absolute";
                this.domNode.classList.add("buttonBar-hasPreNeighbor");
                this.domNode.appendChild(node);
            }
            this.layout();
        },

        getPreNeighbor: function () {
            return this._preNeighbor;
        },

        setPostNeighbor: function (node) {
            if (this._postNeighbor === node) {
                return;
            }
            if (this._postNeighbor) {
                LayoutUtils.removeFromParent(this._postNeighbor);
                delete this._postNeighborBox;
            }
            this._postNeighbor = node;
            if (node) {
                domStyle.set(node, {position: "absolute"});
                domConstruct.place(node, this.domNode);
            }
            // Need to investigate why the following causes problems with the placement of the document container actions drop down
            // this.layout();
        },

        getPostNeighbor: function () {
            return this._postNeighbor;
        },

        setFiller: function (widget) {
            if (widget === this._fillerWidget) {
                return;
            }
            if (this._fillerWidget) {
                this.removeChild(this._fillerWidget);
                // Clean up style changes made to position the filler
                domStyle.set(this._fillerWidget.domNode, {
                        top: "auto",
                        left: "auto",
                        position: "static"}
                );
                delete this._fillerWidget;
            }
            if (widget) {
                domStyle.set(widget.domNode, {position: "absolute"});
                this._fillerWidget = widget;
                this.addChild(widget);
            }
            this.layout();
        },

        /**
         * Adds a button at a specified position (point or index) and immediately begins a drag
         * operation so that the button's position can be adjusted.
         */
        dragIn: function (button, properties, pointOrIndex) {
            var index, barPosition, x, y , buttonBox, buttonInfo;
            var type = typeof pointOrIndex;
            if (type === "number") {
                index = pointOrIndex;
            } else {
                // Find the index for the button by comparing the mouse position to the existing
                // button boxes.
                barPosition = domGeometry.position(this.domNode);
                x = pointOrIndex.x - barPosition.x;
                y = pointOrIndex.y - barPosition.y;
                for (index = 0; index < this._buttonInfo.length; index++) {
                    buttonBox = this._buttonInfo[index].currentBox;
                    if (this.orientation === "horizontal") {
                        if (x < (buttonBox.l + buttonBox.w / 2)) {
                            break;
                        }
                    } else {
                        if (y < (buttonBox.t + buttonBox.h / 2)) {
                            break;
                        }
                    }
                }
            }
            this.addButton(button, properties, false /* scrollToShow */, index, true /* immediate */);

            // Initialize the drag state so that continued dragging can reposition the button or drag it back out.
            if (type !== "number" && (this.orientation === "vertical" || barPosition.h > 0)) {
                // Start the drag at the location of the button center
                buttonInfo = this._buttonInfo[this._indexFromButton(button)];
                x = barPosition.x + buttonInfo.currentBox.l + buttonInfo.currentBox.w / 2;
                y = barPosition.y + buttonInfo.currentBox.t + buttonInfo.currentBox.h / 2;
                this._beginButtonDragAtPoint(buttonInfo, x, y);
                this._buttonDragInfo.hasOrderChanged = true;  // Force a large drag off threshold
                // Simulate drag to the actual mouse location, causing the button center to move under the mouse
                this._continueButtonDrag({pageX: pointOrIndex.x, pageY: pointOrIndex.y});
            }
        },

        _bindButtonProperties: function (buttonInfo, properties) {
            var that = this;
            var labelWatch, textWatch, checkWatch, indexWatch;
            var handleLabelUpdate = function () {
                // Clear the cached size to force update
                delete buttonInfo.preferredSize;
                that.layout();
            };
            // Some buttons use "label" and some use "text".  Watch both for changes.
            labelWatch = buttonInfo.button.watch("label", handleLabelUpdate);
            textWatch  = buttonInfo.button.watch("text", handleLabelUpdate);
            checkWatch = buttonInfo.button.watch("checked", function (name, oldValue, newValue) {
                if (newValue) {
                    that._scrollIntoView(that._buttonInfo.indexOf(buttonInfo));
                }
            });
            indexWatch = properties.watch(LayoutChildProperty.INDEX, function(name, oldValue, newValue) {
                if (!that._isUpdatingIndices && oldValue !== newValue && oldValue === that._buttonInfo.indexOf(buttonInfo) && newValue < that._buttonInfo.length) {
                    var setId = that._setIdFromProperties(buttonInfo.properties);
                    var referenceIndex = setId ? that._indexOfFirstInSet(setId) : 0;
                    that._changeButtonIndices(oldValue + referenceIndex, newValue + referenceIndex, 1);
                    that.layout();
                }
            });
            buttonInfo.unbindButtonProperties = function () {
                labelWatch.unwatch();
                textWatch.unwatch();
                checkWatch.unwatch();
                indexWatch.unwatch();
            };
        },

        _indexFromButton: function (button) {
            var i;
            for (i = 0; i < this._buttonInfo.length; i++) {
                if (this._buttonInfo[i].button === button) {
                    return i;
                }
            }
            return -1;
        },

        _indexFromProperties: function (properties) {
            var i;
            for (i = 0; i < this._buttonInfo.length; i++) {
                if (this._buttonInfo[i].properties === properties) {
                    return i;
                }
            }
            return -1;
        },

        _setIdFromProperties: function (properties) {
            return properties[this.setFacet];
        },

        _indexOfFirstInSet: function (setId) {
            var i;
            for (i = 0; i < this._buttonInfo.length; i++) {
                if (this._setIdFromProperties(this._buttonInfo[i].properties) === setId) {
                    break;
                }
            }
            return i >= this._buttonInfo.length ? -1 : i;
        },

        _indexOfLastInSet: function (setId) {
            var i;
            for (i = this._buttonInfo.length - 1; i >= 0; i--) {
                if (this._setIdFromProperties(this._buttonInfo[i].properties) === setId) {
                    break;
                }
            }
            return i;
        },

        _getSetMemberCount: function (setId) {
            var first = this._indexOfFirstInSet(setId);
            return first < 0 ? 0 : (this._indexOfLastInSet(setId) - first + 1);
        },

        resize: function () {
            // Cache the computed style
            if (!this.computedStyle) {
                this.computedStyle = domStyle.getComputedStyle(this.domNode);
            }
            this._clearButtonClipping();
            this.inherited(arguments);
        },

        layout: function() {
            if (!this.domNode || (this._buttonDragInfo && this._buttonDragInfo.hasMoved) || this._addPending || this._removePending) {
                return;
            }
            var requiredIndex;
            if (this._justAddedIndex !== undefined) {
                requiredIndex = this._justAddedIndex;
                delete this._justAddedIndex;
            } else {
                requiredIndex = this._getCheckedIndex();
            }
            if (!this._computeLayout(requiredIndex)) {
                return;
            }
            var i, buttonInfo;
            for (i = 0; i < this._buttonInfo.length; i++) {
                buttonInfo = this._buttonInfo[i];
                domGeometry.setMarginBox(buttonInfo.button.domNode, buttonInfo.targetBox);
                Geometry.copyBox(buttonInfo.targetBox, buttonInfo.currentBox);
                this._updateToolTip(buttonInfo);
            }

            // Position the scroll buttons
            domGeometry.setMarginBox(this._previousButton, this._previousButtonBox);
            domGeometry.setMarginBox(this._nextButton, this._nextButtonBox);

            if (this._plusButtonInfo) {
                domGeometry.setMarginBox(this._plusButtonInfo.button.domNode, this._plusButtonInfo.targetBox);
                Geometry.copyBox(this._plusButtonInfo.targetBox, this._plusButtonInfo.currentBox);
            }
            if (this._preNeighbor) {
                domGeometry.setMarginBox(this._preNeighbor, this._preNeighborBox);
            }
            if (this._postNeighbor) {
                domGeometry.setMarginBox(this._postNeighbor, this._postNeighborBox);
            }
            if (this._fillerWidget) {
                if (this._fillerWidget.resize) {
                    this._fillerWidget.resize(this._fillerBox);
                } else {
                    domGeometry.setMarginBox(this._fillerWidget.domNode, this._fillerBox);
                }
            }
            this._updateScrollButtonVisibility();
            this._updateButtonVisibility();
        },

        _updateToolTip: function (buttonInfo) {
            if (this.displayToolTipsWhenTruncated && !buttonInfo.hasToolTip) {
                var toolTip = this._isTruncated(buttonInfo) ? buttonInfo.button.get("label") : null;
                if (toolTip !== domAttr.get(buttonInfo.button.domNode, "title")) {
                    if (toolTip) {
                        domAttr.set(buttonInfo.button.domNode, "title", toolTip);
                    } else {
                        domAttr.remove(buttonInfo.button.domNode, "title", toolTip);
                    }
                }
            }
        },

        _isTruncated: function (buttonInfo) {
            return (this.orientation === "horizontal" && buttonInfo.currentBox.w < buttonInfo.preferredSize.w) ||
                   (this.orientation === "vertical" && buttonInfo.currentBox.h < buttonInfo.preferredSize.h);
        },

        getPreferredSize: function () {
            var i, buttonInfo, buttonSize, prototype;
            var isHorizontal = this.orientation === "horizontal";
            var plusSize = this._getPlusButtonSize();
            var preSize = this._getPreNeighborSize();
            var postSize = this._getPostNeighborSize();
            var size = {w: plusSize.w, h: plusSize.h};
            if (isHorizontal) {
                size.w += preSize.w;
                size.w += postSize.w;
            } else {
                size.h += preSize.h;
                size.h += postSize.h;
            }
            if (!this._buttonInfo.length) {
                if (isHorizontal) {
                    prototype = this._getPrototypeButton();
                    if (prototype) {
                        prototype.set("label", "Prototype");
                        buttonSize = domGeometry.getMarginBox(prototype.domNode);
                        if (buttonSize.h > size.h) {
                            size.h = buttonSize.h;
                        }
                    }
                }
            }
            for (i = 0; i < this._buttonInfo.length; i++) {
                buttonInfo = this._buttonInfo[i];
                buttonSize = this._getButtonSize(buttonInfo);
                if (isHorizontal) {
                    size.w += buttonSize.w;
                    if (buttonSize.h > size.h) {
                        size.h = buttonSize.h;
                    }
                } else {
                    size.h += buttonSize.h;
                    if (buttonSize.w > size.w) {
                        size.w = buttonSize.w;
                    }
                }
            }
            return size;
        },

        _computeLayout: function (requiredIndex) {
            // Cache the computed style
            if (!this.computedStyle) {
                this.computedStyle = domStyle.getComputedStyle(this.domNode);
            }
            var barBox = domGeometry.getMarginBox(this.domNode, this.computedStyle);
            if (!barBox || (barBox.h === 0 && barBox.w === 0)) {
                return false;
            }
            barBox.l = 0;
            barBox.t = 0;

            var availWidth, availHeight, totalWidth, totalHeight, overage, excessWidth, excessHeight, reduction,
                reducedWidth, reducedHeight, shedWidth, shedHeight, range, stopIndex, distributedWidth, shrunkSingleButton;
            var x = 0;
            var y = 0;

            // First gather all the preferred button sizes
            var i, buttonInfo, buttonSize, setId;
            var buttonCount = this._buttonInfo.length;
            var totalGap = 0;
            var previousSet;
            for (i = 0; i < buttonCount; i++) {
                buttonInfo = this._buttonInfo[i];
                buttonSize = this._getButtonSize(buttonInfo);
                buttonInfo.targetBox.w = buttonSize.w;
                buttonInfo.targetBox.h = buttonSize.h;
                buttonInfo.targetBox.l = 0;
                buttonInfo.targetBox.t = 0;
                buttonInfo.preGap = 0;
                setId = this._setIdFromProperties(buttonInfo.properties);
                if (i > 0 && setId !== previousSet) {
                    buttonInfo.preGap = this.setGap;
                    totalGap += this.setGap;
                }
                previousSet = setId;
            }

            // Gather sizes of other elements
            this._getPlusButtonSize();
            this._getPreNeighborSize();
            this._getPostNeighborSize();

            if (this.orientation === "horizontal") {
                // Compute the width required to show all buttons and while looping through the buttons
                // also set their heights to the bar height
                availWidth = barBox.w;
                if (this._plusButtonInfo) {
                    availWidth -= this._plusButtonInfo.preferredSize.w;
                }
                // The post neighbor position computed before pre neighbor position since the latter computation
                // updates the barBox width which would break the former computation.
                if (this._postNeighbor) {
                    availWidth -= this._postNeighborBox.w;
                    this._postNeighborBox.l = barBox.w - this._postNeighborBox.w;
                    barBox.w -= this._postNeighborBox.w;
                }
                if (this._preNeighbor) {
                    availWidth -= this._preNeighborBox.w;
                    x += this._preNeighborBox.w;
                    this._preNeighborBox.h = barBox.h;  // This is inconsistent with the postNeighbor box computation
                                                        // At the moment it is necessary because the tab bar grip is
                                                        // set as a pre-neighbor and it lacks a default height.
                    barBox.w -= this._preNeighborBox.w;
                    barBox.l += this._preNeighborBox.w;
                }

                if (this.distributeButtons && buttonCount > 0) {
                    // The buttons are to be distributed so as to occupy the entire bar.  Update their
                    // preferred widths accordingly.
                    distributedWidth = Math.max(availWidth / buttonCount, this.minimumButtonWidth);
                    for (i = 0; i < buttonCount; i++) {
                        this._buttonInfo[i].targetBox.w = distributedWidth;
                    }
                }

                totalWidth = totalGap;
                overage = 0;
                for (i = 0; i < buttonCount; i++) {
                    buttonInfo = this._buttonInfo[i];
                    totalWidth += buttonInfo.targetBox.w;
                    buttonInfo.targetBox.h = barBox.h;
                    if (buttonInfo.targetBox.w > this.minimumButtonWidth) {
                        overage += buttonInfo.targetBox.w - this.minimumButtonWidth;
                    }
                }

                // Shrink the buttons to fit, each in proportion to its excess over the minimum width
                if (totalWidth > availWidth) {
                    excessWidth = totalWidth - availWidth;
                    for (i = 0; i < buttonCount; i++) {
                        buttonInfo = this._buttonInfo[i];
                        if ((this.shrinkWidthToFit || buttonInfo.properties.get(LayoutChildProperty.TITLE_SHORTENING_ALLOWED)) &&
                            buttonInfo.targetBox.w > this.minimumButtonWidth) {
                            reduction    = excessWidth * (buttonInfo.targetBox.w - this.minimumButtonWidth) / overage + 1;
                            reducedWidth = Math.max(buttonInfo.targetBox.w - reduction, this.minimumButtonWidth);
                            totalWidth -= buttonInfo.targetBox.w - reducedWidth;
                            buttonInfo.targetBox.w = reducedWidth;
                        }
                    }
                }

                if (totalWidth > availWidth) {
                    // Not all buttons fit.  Choose a range to keep visible.
                    range = {low: 0, high: buttonCount - 1};

                    // Make adjustments to account for scroll buttons
                    x += this._scrollButtonWidth;
                    availWidth -= 2 * this._scrollButtonWidth;

                    if (this._showingRange) {
                        // Shed buttons from the end until one of the following occurs:
                        //   1. The previous end is encountered
                        //   2. The "required" button is encountered
                        //   3. Sufficient space has been reclaimed
                        stopIndex = this._showingRange.high;
                        if (requiredIndex >= 0 && requiredIndex > stopIndex) {
                            stopIndex = requiredIndex;
                        }
                        for (; range.high > stopIndex && totalWidth > availWidth; range.high--) {
                            buttonInfo = this._buttonInfo[range.high];
                            shedWidth = buttonInfo.targetBox.w + buttonInfo.preGap;
                            totalWidth -= shedWidth;
                        }

                        // Next shed from the beginning using the above criteria, except for the third one.
                        stopIndex = this._showingRange.low;
                        if (requiredIndex >= 0 && requiredIndex < stopIndex) {
                            stopIndex = requiredIndex;
                        }
                        for (; range.low < stopIndex && totalWidth > availWidth; range.low++) {
                            buttonInfo = this._buttonInfo[range.low];
                            shedWidth = buttonInfo.targetBox.w + buttonInfo.preGap;
                            totalWidth -= shedWidth;
                            x -= shedWidth;
                        }
                    }

                    // Continue shedding from the end until
                    stopIndex = range.low;
                    if (requiredIndex >= 0 && requiredIndex > stopIndex ) {
                        stopIndex = requiredIndex;
                    }
                    for (; range.high > stopIndex && totalWidth > availWidth; range.high--) {
                        buttonInfo = this._buttonInfo[range.high];
                        shedWidth = buttonInfo.targetBox.w + buttonInfo.preGap;
                        totalWidth -= shedWidth;
                    }

                    // Continue shedding from the beginning as needed
                    stopIndex = range.high;
                    if (requiredIndex >= 0 && requiredIndex < stopIndex) {
                        stopIndex = requiredIndex;
                    }
                    for (; range.low < stopIndex && totalWidth > availWidth; range.low++) {
                        buttonInfo = this._buttonInfo[range.low];
                        shedWidth = buttonInfo.targetBox.w + buttonInfo.preGap;
                        totalWidth -= shedWidth;
                        x -= shedWidth;
                    }

                    // If the range is restricted to showing a single button shrink it as necessary,
                    // even if shrink to fit is not enabled
                    if (range.low === range.high) {
                        buttonInfo = this._buttonInfo[range.low];
                        if (!this.shrinkWidthToFit && !buttonInfo.properties.get(LayoutChildProperty.TITLE_SHORTENING_ALLOWED) &&
                            availWidth < buttonInfo.targetBox.w && buttonInfo.targetBox.w > this.minimumButtonWidth) {
                            reduction    = buttonInfo.targetBox.w - availWidth;
                            reducedWidth = Math.max(buttonInfo.targetBox.w - reduction, this.minimumButtonWidth);
                            totalWidth -= buttonInfo.targetBox.w - reducedWidth;
                            buttonInfo.targetBox.w = reducedWidth;
                            shrunkSingleButton = true;
                        }
                    }

                    // If the range is only restricted at the low end use any excess space
                    // to partially display the preceding button.
                    if (!shrunkSingleButton && range.low > 0 && range.high === buttonCount - 1) {
                        x += availWidth - totalWidth;
                    }
                }
                // Set the button positions
                for (i = 0; i < buttonCount; i++) {
                    buttonInfo = this._buttonInfo[i];
                    buttonInfo.targetBox.l = x + buttonInfo.preGap;
                    x += buttonInfo.targetBox.w + buttonInfo.preGap;
                }

                // Compute positions for the scroll buttons (regardless of whether they are actually visible)
                if (!this._scrollButtonWidth) { // undefined or 0
                    this._updateScrollButtonDimension("width");
                }
                this._previousButtonBox = {l: barBox.l, t: 0, w: this._scrollButtonWidth, h: barBox.h};
                this._nextButtonBox = {l: barBox.l + barBox.w - this._scrollButtonWidth, t: 0, w: this._scrollButtonWidth, h: barBox.h};

                if (this._plusButtonInfo) {
                    this._plusButtonInfo.targetBox.t = 0;
                    this._plusButtonInfo.targetBox.h = barBox.h;
                    this._plusButtonInfo.targetBox.w = this._plusButtonInfo.preferredSize.w;
                    if (range) {
                        // The range is restricted.  Place the plus button at the extreme right
                        this._plusButtonInfo.targetBox.l = barBox.l + barBox.w - this._plusButtonInfo.preferredSize.w;
                        this._nextButtonBox.l -= this._plusButtonInfo.preferredSize.w;
                    } else {
                        // Place the plus button immediately after the last button.
                        this._plusButtonInfo.targetBox.l = x;
                        x += this._plusButtonInfo.preferredSize.w;
                    }
                }

                if (this._fillerWidget) {
                    this._fillerBox = {
                        l: x,
                        t: 0,
                        w: barBox.w - x,
                        h: barBox.h
                    }
                }
            } else {  // orientation === "vertical"
                // Compute the height required to show all buttons and while looping through the buttons
                // also set their width to the bar width
                availHeight = barBox.h;
                if (this._plusButtonInfo) {
                    availHeight -= this._plusButtonInfo.preferredSize.h;
                }
                if (this._preNeighbor) {
                    availHeight -= this._preNeighborBox.h;
                    y += this._preNeighborBox.h;
                    this._preNeighborBox.w = barBox.w;
                    barBox.h -= this._preNeighborBox.h;
                    barBox.t += this._preNeighborBox.h;
                }
                if (this._postNeighbor) {
                    availHeight -= this._postNeighborBox.h;
                    this._postNeighborBox.t = barBox.h - this._postNeighborBox.h;
                    this._postNeighborBox.w = barBox.w;
                    barBox.h -= this._postNeighborBox.h;
                }
                totalHeight = totalGap;
                overage = 0;
                for (i = 0; i < buttonCount; i++) {
                    buttonInfo = this._buttonInfo[i];
                    totalHeight += buttonInfo.targetBox.h;
                    buttonInfo.targetBox.w = barBox.w;
                    if (buttonInfo.targetBox.h > this.minimumButtonHeight) {
                        overage += buttonInfo.targetBox.h - this.minimumButtonHeight;
                    }
                }

                // Shrink the buttons to fit, each in proportion to its excess over the minimum height
                if (totalHeight > availHeight) {
                    excessHeight = totalHeight - barBox.h;
                    for (i = 0; i < buttonCount; i++) {
                        buttonInfo = this._buttonInfo[i];
                        if ((this.shrinkHeightToFit || buttonInfo.properties.get(LayoutChildProperty.TITLE_SHORTENING_ALLOWED)) &&
                            buttonInfo.targetBox.h > this.minimumButtonHeight) {
                            reduction    = excessHeight * (buttonInfo.targetBox.h - this.minimumButtonHeight) / overage + 1;
                            reducedHeight = Math.max(buttonInfo.targetBox.h - reduction, this.minimumButtonHeight);
                            totalHeight -= buttonInfo.targetBox.h - reducedHeight;
                            buttonInfo.targetBox.h = reducedHeight;
                        }
                    }
                }

                if (totalHeight > availHeight && buttonCount > 1) {
                    // Not all buttons fit.  Choose a range to keep visible.
                    range = {low: 0, high: buttonCount - 1};

                    // Make adjustments to account for scroll buttons
                    y += this._scrollButtonHeight;
                    availHeight -= 2 * this._scrollButtonHeight;

                    if (this._showingRange) {
                        // Shed buttons from the end until one of the following occurs:
                        //   1. The previous end is encountered
                        //   2. The "required" button is encountered
                        //   3. Sufficient space has been reclaimed
                        stopIndex = this._showingRange.high;
                        if (requiredIndex >= 0 && requiredIndex > stopIndex) {
                            stopIndex = requiredIndex;
                        }
                        for (; range.high > stopIndex && totalHeight > availHeight; range.high--) {
                            buttonInfo = this._buttonInfo[range.high];
                            shedHeight = buttonInfo.targetBox.h + buttonInfo.preGap;
                            totalHeight -= shedHeight;
                        }

                        // Next shed from the beginning using the above criteria, except for the third one.
                        stopIndex = this._showingRange.low;
                        if (requiredIndex >= 0 && requiredIndex < stopIndex) {
                            stopIndex = requiredIndex;
                        }
                        for (; range.low < stopIndex && totalHeight > availHeight; range.low++) {
                            buttonInfo = this._buttonInfo[range.low];
                            shedHeight = buttonInfo.targetBox.h + buttonInfo.preGap;
                            totalHeight -= shedHeight;
                            y -= shedHeight;
                        }
                    }

                    // Continue shedding from the end until
                    stopIndex = range.low;
                    if (requiredIndex >= 0 && requiredIndex > stopIndex ) {
                        stopIndex = requiredIndex;
                    }
                    for (; range.high > stopIndex && totalHeight > availHeight; range.high--) {
                        buttonInfo = this._buttonInfo[range.high];
                        shedHeight = buttonInfo.targetBox.h + buttonInfo.preGap;
                        totalHeight -= shedHeight;
                    }

                    // Continue shedding from the beginning as needed
                    stopIndex = range.high;
                    if (requiredIndex >= 0 && requiredIndex < stopIndex) {
                        stopIndex = requiredIndex;
                    }
                    for (; range.low < stopIndex && totalHeight > availHeight; range.low++) {
                        buttonInfo = this._buttonInfo[range.low];
                        shedHeight = buttonInfo.targetBox.h + buttonInfo.preGap;
                        totalHeight -= shedHeight;
                        y -= shedHeight;
                    }

                    // If the range is only restricted at the low end use any excess space
                    // to partially display the preceding button.
                    if (range.low > 0 && range.high === buttonCount - 1) {
                        y += availHeight - totalHeight;
                    }
                }
                // Set the button positions
                for (i = 0; i < buttonCount; i++) {
                    buttonInfo = this._buttonInfo[i];
                    buttonInfo.targetBox.t = y + buttonInfo.preGap;
                    y += buttonInfo.targetBox.h + buttonInfo.preGap;
                }

                // Compute positions for the scroll buttons (regardless of whether they are actually visible)
                if (!this._scrollButtonHeight) { // undefined or 0
                    this._updateScrollButtonDimension("height");
                }
                this._previousButtonBox = {l: 0, t: barBox.t, w: barBox.w, h: this._scrollButtonHeight};
                this._nextButtonBox = {l:0, t: barBox.t + barBox.h - this._scrollButtonHeight, w: barBox.w, h: this._scrollButtonHeight};

                if (this._plusButtonInfo) {
                    this._plusButtonInfo.targetBox.l = 0;
                    this._plusButtonInfo.targetBox.w = barBox.w;
                    this._plusButtonInfo.targetBox.h = this._plusButtonInfo.preferredSize.h;
                    if (range) {
                        // The range is restricted.  Place the plus button at the extreme bottom
                        this._plusButtonInfo.targetBox.t = barBox.t + barBox.h - this._plusButtonInfo.preferredSize.h;
                        this._nextButtonBox.t -= this._plusButtonInfo.preferredSize.h;
                    } else {
                        // Place the plus button immediately after the last button.
                        this._plusButtonInfo.targetBox.t = y;
                        y += this._plusButtonInfo.preferredSize.h;
                    }
                }
                if (this._fillerWidget) {
                    this._fillerBox = {
                        l: 0,
                        t: y,
                        w: barBox.w,
                        h: barBox.h - y
                    }
                }
            }
            this._showingRange = range;
            return true;
        },

        _getCheckedIndex: function () {
            var i;
            for (i = 0; i < this._buttonInfo.length; i++) {
                if (this._buttonInfo[i].button.get("checked")) {
                    return i;
                }
            }
            return -1;
        },

        _getButtonSize: function (buttonInfo) {
            if (!buttonInfo.preferredSize) {
                buttonInfo.preferredSize = this._measureButton(buttonInfo);
            }
            return buttonInfo.preferredSize;
        },

        _getPlusButtonSize: function () {
            if (!this._plusButtonInfo) {
                return {w: 0, h: 0};
            }
            if (!this._plusButtonInfo.preferredSize) {
                this._plusButtonInfo.preferredSize = domGeometry.getMarginBox(this._plusButtonInfo.button.domNode);
            }
            return this._plusButtonInfo.preferredSize;
        },

        _getPreNeighborSize: function () {
            if (!this._preNeighbor) {
                return {w: 0, h: 0};
            }
            if (!this._preNeighborBox) {
                this._preNeighborBox = this._getDefaultSize(this._preNeighbor);
            }
            return this._preNeighborBox;
        },

        _getPostNeighborSize: function () {
            if (!this._postNeighbor) {
                return {w: 0, h: 0};
            }
            if (!this._postNeighborBox) {
                this._postNeighborBox = this._getDefaultSize(this._postNeighbor);
            }
            return this._postNeighborBox;
        },

        /**
         * Subclasses should either override this method or both the _createPrototypeButton and _configurePrototypeButton
         * methods defined below.
         */
        _measureButton: function (buttonInfo) {
            this._getPrototypeButton();
            if (!this._prototypeButton) {
                return {w: 80, h:24};
            } else {
                this._configurePrototypeButton(this._prototypeButton, buttonInfo.button, buttonInfo.properties);
                return domGeometry.getMarginBox(this._prototypeButton.domNode);
            }
        },

        _getPrototypeButton: function () {
            if (!this._prototypeButton) {
                this._prototypeButton = this._createPrototypeButton();
                if (this._prototypeButton) {
                    domStyle.set(this._prototypeButton.domNode, {position: "absolute", visibility : "hidden"});
                    this.addChild(this._prototypeButton);
                }
            }
            return this._prototypeButton;
        },

        _getDefaultSize: function (node) {
            var box = domGeometry.getMarginBox(node);
            box.defaultW = box.w;
            box.defaultH = box.h;
            return box;
        },

        _diffBox: function (box1, box2) {
            return {l: box1.l - box2.l,
                    t: box1.t - box2.t,
                    w: box1.w - box2.w,
                    h: box1.h - box2.h};
        },

        _updateButtonVisibility: function () {
            var i, showing, partial, buttonInfo, bounds, clipBox;
            var buttonCount = this._buttonInfo.length;
            var range = this._showingRange;
            for (i = 0; i < buttonCount; i++) {
                buttonInfo = this._buttonInfo[i];
                showing = false;
                partial = false;
                if (!range || (this._buttonDragInfo && this._buttonDragInfo.buttonInfo[0] === buttonInfo)) {
                    showing = true;
                } else if (i >= range.low && i <= range.high) {
                    showing = true;
                } else if (i === range.high + 1 ) {
                    showing = true;
                    partial = true;
                } else if (i === range.low - 1 && range.high === buttonCount - 1) {
                    showing = true;
                    partial = true;
                }

                if (showing !== buttonInfo.isShowing) {
                    domStyle.set(buttonInfo.button.domNode, {visibility : showing ? "visible" : "hidden"});
                    buttonInfo.isShowing = showing;
                }

                if (partial !== buttonInfo.isClipped) {
                    if (!partial) {
                        domStyle.set(buttonInfo.button.domNode, {clip : "auto"});
                    } else {
                        bounds = this._computeScrollBounds(this._getBarBounds());
                        clipBox = Geometry.computeClip(buttonInfo.targetBox, bounds);
                        if (this._transformClipBox) {
                            this._transformClipBox(clipBox, buttonInfo.targetBox);
                        }
                        domStyle.set(buttonInfo.button.domNode, LayoutUtils.formatClipStyle(clipBox));
                    }
                    buttonInfo.isClipped = partial;
                }
            }
        },

        _updateScrollButtonVisibility: function () {
            if (!this._showingRange) {
                domStyle.set(this._previousButton, {display: "none"});
                domStyle.set(this._nextButton, {display: "none"});
                if (this._fillerWidget) {
                    domStyle.set(this._fillerWidget.domNode, {visibility: "visible"});
                }
            } else {
                domStyle.set(this._previousButton, {display: "block"});
                domStyle.set(this._nextButton, {display: "block"});
                if (this._fillerWidget) {
                    domStyle.set(this._fillerWidget.domNode, {visibility: "hidden"});
                }
                if (this._showingRange.low > 0) {
                    domClass.remove(this._previousButton, "disabled");
                } else {
                    domClass.add(this._previousButton, "disabled");
                }
                if (this._showingRange.high < (this._buttonInfo.length - 1)) {
                    domClass.remove(this._nextButton, "disabled");
                } else {
                    domClass.add(this._nextButton, "disabled");
                }
            }
        },

        /**
         * Subclasses can override this to create a prototype button that will be used to measure the
         * children.
         */
        _createPrototypeButton: function () {
            return null;
        },

        /**
         * Subclasses that override _createPrototypeButton to create a button whose configuration required more than
         * simply setting its label should also override this method.
         */
        _configurePrototypeButton: function (prototype, button /*, properties */) {
            prototype.set("label", button.get("label"));
            prototype.set("text", button.get("text"));
        },

        _updateScrollButtonIcons: function () {
            if (this.orientation === "horizontal") {
                domClass.remove(this._previousButton, "scrollUpButton");
                domClass.remove(this._nextButton, "scrollDownButton");
                domClass.add(this._previousButton, "scrollLeftButton");
                domClass.add(this._nextButton, "scrollRightButton");
            } else {
                domClass.remove(this._previousButton, "scrollLeftButton");
                domClass.remove(this._nextButton, "scrollRightButton");
                domClass.add(this._previousButton, "scrollUpButton");
                domClass.add(this._nextButton, "scrollDownButton");
            }
        },

        _updateNeighbors: function () {
            if (this._preNeighborBox) {
                this._preNeighborBox.w = this._preNeighborBox.defaultW;
                this._preNeighborBox.h = this._preNeighborBox.defaultH;
            }
            if (this._postNeighborBox) {
                this._postNeighborBox.w = this._postNeighborBox.defaultW;
                this._postNeighborBox.h = this._postNeighborBox.defaultH;
            }
        },

        _setOrientationAttr: function(orientation) {
            if (orientation !== this.orientation) {
                this._abortAnimation();
                this._set("orientation", orientation);
                this._updateScrollButtonIcons();
                this._updateNeighbors();
                //this.layout();
            }

            this.domNode.setAttribute("aria-orientation", orientation);
        },

        _setShrinkWidthToFitAttr: function (shrinkToFit) {
            if (shrinkToFit !== this.shrinkWidthToFit) {
                this._set("shrinkWidthToFit", shrinkToFit);
                this.layout();
            }
        },

        _setShrinkHeightToFitAttr: function (shrinkToFit) {
            if (shrinkToFit !== this.shrinkHeightToFit) {
                this._set("shrinkHeightToFit", shrinkToFit);
                this.layout();
            }
        },

        _setMinimumButtonWidthAttr: function (width) {
            if (width !== this.minimumButtonWidth) {
                this._set("minimumButtonWidth", width);
                this.layout();
            }
        },

        _setMinimumButtonHeightAttr: function (height) {
            if (height !== this.minimumButtonHeight) {
                this._set("minimumButtonHeight", height);
                this.layout();
            }
        },

        _handleScrollButtonPress: function (e) {
            this._scrollInfo = {
                button: e.target,
                connection: on(this.ownerDocument, touch.release, lang.hitch(this, "_handleScrollButtonRelease")),
                timer: setTimeout(lang.hitch(this, "_handleScrollButtonTimeout", e), LONG_PRESS_THRESHOLD)
            };
            LayoutUtils.stopEvent(e);
        },

        _handleScrollButtonRelease: function (e) {
            if (this._scrollInfo && this._scrollInfo.button === e.target) {
                if (e.target === this._previousButton) {
                    this._scrollToPrevious();
                } else {
                    this._scrollToNext();
                }
                this._cleanupScrollInfo();
            }
        },

        _handleScrollButtonTimeout: function () {
            var i, menu, item, button, clickHandler;
            if (this._scrollInfo) {
                SelectionBlocker.blockSelection(this.domNode);

                popup.close();

                menu = new ContextMenu({
                    targetNodes: [this._scrollInfo.button],
                    id: this.id + "ScrollMenu"
                });
                for (i = 0; i < this._buttonInfo.length; i++) {
                    button = this._buttonInfo[i].button;
                    clickHandler = function (buttonToCheck) {
                        return function () {
                            buttonToCheck.set("checked", true);
                        }
                    }(button);
                    item = new MenuItem({
                        text: button.get("label")
                    });
                    domClass.add(item.domNode, i >= this._showingRange.low && i <= this._showingRange.high ? "showingButtonBarOverflowItem" : "hiddenButtonBarOverflowItem");
                    if (button.get("checked")) {
                        domClass.add(item.domNode, "checkedButtonBarOverflowItem");
                    }
                    on(item.domNode, touch.release, clickHandler);
                    on(item.domNode, "click", clickHandler);
                    menu.addChild(item);
                }
                menu.open({target: this._scrollInfo.button});
                menu.on("close", function() {
                    // Destroying immediately causes error in menu close code
                    setTimeout(function() {
                        menu.destroyRecursive();
                    }, 0);
                    SelectionBlocker.unblockSelection();
                });
                this._cleanupScrollInfo();
            }
        },

        _cleanupScrollInfo: function () {
            if (this._scrollInfo) {
                clearTimeout(this._scrollInfo.timer);
                this._scrollInfo.connection.remove();
                delete this._scrollInfo;
            }
        },

        _scrollToPrevious: function () {

            if (this._showingRange.low > 0) {
                this._scrollIntoView(this._showingRange.low - 1);
            }
        },

        _scrollToNext: function () {
            if (this._showingRange.high < (this._buttonInfo.length - 1)) {
                this._scrollIntoView(this._showingRange.high + 1);
            }
        },

        _scrollIntoView: function (buttonIndex) {
            if (this._showingRange && (buttonIndex < this._showingRange.low || buttonIndex > this._showingRange.high)) {
                var i;
                this._computeLayout(buttonIndex);

                // Animate to new positions
                var startTime = new Date().valueOf();
                for (i = 0; i < this._buttonInfo.length; i++) {
                    this._setupAnimation(this._buttonInfo[i], startTime);
                }
                this._startAnimation();
            }
        },

        _setupAnimation: function (buttonInfo, startTime) {
            startTime = startTime || new Date().valueOf();
            var boxDiff = this._diffBox(buttonInfo.targetBox, buttonInfo.currentBox);
            buttonInfo.animationInfo = {
                startBox: lang.clone(buttonInfo.currentBox),
                startTime: startTime,
                duration: this._computeButtonAnimationDuration(boxDiff),
                boxDiff: boxDiff
            }
        },

        _startAnimation: function () {
            if (!(this.animationEnabled && LayoutUtils.isAnimationEnabled())) {
                this._updateButtonPositions();
            } else if (!this._buttonAnimationTimer) {
                this._buttonAnimationTimer = setInterval(lang.hitch(this, "_updateButtonPositions"), AnimationTiming.UPDATE_INTERVAL);
            }
        },

        _endAnimation: function () {
            if (this._buttonAnimationTimer) {
                clearInterval(this._buttonAnimationTimer);
                delete this._buttonAnimationTimer;
                this._clearButtonClipping();
                if (this._animationDoneResponse) {
                    this._animationDoneResponse();
                    delete this._animationDoneResponse;
                }
            }
        },

        _abortAnimation: function () {
            if (this._buttonAnimationTimer) {
                clearInterval(this._buttonAnimationTimer);
                delete this._buttonAnimationTimer;
                this._clearButtonClipping();
            }
        },

        _updateButtonPositions: function () {
            var continueAnimating = false;
            var currentTime = new Date().valueOf();
            var i, buttonInfo;
            var barBounds = this._getBarBounds();
            for (i = 0; i < this._buttonInfo.length; i++) {
                buttonInfo = this._buttonInfo[i];
                    if (this._updateButtonPosition(buttonInfo, currentTime, barBounds)) {
                        continueAnimating = true;
                }
            }
            if (this._plusButtonInfo) {
                if (this._updateButtonPosition(this._plusButtonInfo, currentTime, barBounds)) {
                    continueAnimating = true;
                }
            }
            if (!continueAnimating) {
                this._endAnimation();
                this._updateButtonVisibility();
                this._updateScrollButtonVisibility();
            }
        },

        _updateButtonPosition: function (buttonInfo, currentTime, barBounds) {
            var animationInfo, elapsedTime, box, fraction, bounds, clipBox;
            var continueAnimating = false;
            if (buttonInfo.animationInfo) {
                animationInfo = buttonInfo.animationInfo;
                elapsedTime = currentTime - animationInfo.startTime;
                box = buttonInfo.currentBox;
                if (this.animationEnabled && LayoutUtils.isAnimationEnabled() && elapsedTime < animationInfo.duration) {
                    fraction = easing.cubicInOut(elapsedTime / animationInfo.duration);
                    box.l = animationInfo.startBox.l + animationInfo.boxDiff.l * fraction;
                    box.t = animationInfo.startBox.t + animationInfo.boxDiff.t * fraction;
                    box.w = animationInfo.startBox.w + animationInfo.boxDiff.w * fraction;
                    box.h = animationInfo.startBox.h + animationInfo.boxDiff.h * fraction;
                    continueAnimating = true;
                } else {
                    Geometry.copyBox(buttonInfo.targetBox, box);
                    delete buttonInfo.animationInfo;
                }

                // Show, hide or clip the button as necessary.
                if (buttonInfo !== this._plusButtonInfo) {
                    bounds = this._computeScrollBounds(barBounds);
                    if (!Geometry.boxesOverlap(box, bounds)) {
                        domStyle.set(buttonInfo.button.domNode, {visibility : "hidden"});
                        buttonInfo.isShowing = false;
                    } else {
                        if (!buttonInfo.isShowing) {
                            domStyle.set(buttonInfo.button.domNode, {visibility : "visible"});
                            buttonInfo.isShowing = true;
                        }
                        clipBox = Geometry.computeClip(box, bounds);
                    }
                }
                if (clipBox && (clipBox.w < box.w || clipBox.h < box.h)) {
                    if (this._transformClipBox) {
                        this._transformClipBox(clipBox, buttonInfo.targetBox);
                    }
                    domStyle.set(buttonInfo.button.domNode, LayoutUtils.formatClipStyle(clipBox));
                    buttonInfo.isClipped = true;
                } else if (buttonInfo.isClipped) {
                    domStyle.set(buttonInfo.button.domNode, {clip : "auto"});
                    buttonInfo.isClipped = false;
                }
                domGeometry.setMarginBox(buttonInfo.button.domNode, box);
            }
            return continueAnimating;
        },

        _getBarBounds: function () {
            var barBounds = domGeometry.getMarginBox(this.domNode, this.computedStyle);
            barBounds.l = -1;
            barBounds.t = -1;
            barBounds.w += 2;
            barBounds.h += 2;
            return barBounds;
        },

        _getVisibleButtonSetBounds: function (setId) {
            return Geometry.intersectionBox(this._getButtonSetBounds(setId), this._computeScrollBounds());
        },

        _getButtonSetBounds: function (setId) {
            var firstIndex = this._indexOfFirstInSet(setId);
            var lastIndex  = this._indexOfLastInSet(setId);
            if (firstIndex < 0 || lastIndex < 0) {
                return undefined;
            }
            var firstBox   = this._buttonInfo[firstIndex].currentBox;
            var lastBox    = this._buttonInfo[lastIndex].currentBox;
            var bounds     = this._getBarBounds();
            if (this.orientation === "horizontal") {
                bounds.l = firstBox.l;
                bounds.w = lastBox.l - firstBox.l + lastBox.w;
            } else {
                bounds.t = firstBox.t;
                bounds.h = lastBox.t - firstBox.t + lastBox.h;
            }
            return bounds;
        },

        _computeScrollBounds: function (barBounds) {
            var prevRight, prevBottom, scrollBounds;
            barBounds = barBounds || this._getBarBounds();
            if (this._showingRange) {
                if (this.orientation === "horizontal") {
                    prevRight = this._previousButtonBox.l + this._previousButtonBox.w;
                    scrollBounds = {
                        l: prevRight,
                        t: barBounds.t,
                        w: this._nextButtonBox.l - prevRight,
                        h: barBounds.h
                    };
                } else {
                    prevBottom = this._previousButtonBox.t + this._previousButtonBox.h;
                    scrollBounds = {
                        l: barBounds.l,
                        t: prevBottom,
                        w: barBounds.w,
                        h: this._nextButtonBox.t - prevBottom
                    };
                }
                return Geometry.intersectionBox(barBounds, scrollBounds);
            }
            return barBounds;
        },

        _clearButtonClipping: function () {
            var i, buttonInfo;
            for (i = 0; i < this._buttonInfo.length; i++) {
                buttonInfo = this._buttonInfo[i];
                if (buttonInfo.isClipped) {
                    if (buttonInfo.button.domNode) {
                    domStyle.set(buttonInfo.button.domNode, {clip : "auto"});
                    }
                    buttonInfo.isClipped = false;
                }
            }
        },

        _computeButtonAnimationDuration: function (boxDiff) {
            var maxDiff = Math.max(Math.abs(boxDiff.l), Math.max(Math.abs(boxDiff.t), Math.max(Math.abs(boxDiff.w), Math.abs(boxDiff.h))));
            return AnimationTiming.computeDuration(maxDiff);
        },

        _beginButtonDrag: function (buttonInfo, e) {
            var draggingSet, setId, firstIndex, lastIndex;
            if (mouse.isLeft(e) && this.reorderingEnabled) {
                // If the button being dragged is the only member of its set and other buttons are
                // present then treat this as a set drag.
                if (this._buttonInfo.length > 1) {
                    setId = this._setIdFromProperties(buttonInfo.properties);
                    firstIndex = this._indexOfFirstInSet(setId);
                    lastIndex  = this._indexOfLastInSet(setId);
                    if (firstIndex === lastIndex) {
                        this._beginSetDragAtPoint(setId, null, e.pageX, e.pageY);
                        draggingSet = true;
                        this._buttonDragInfo.initiatedButtonDrag  = true;
                    }
                }
                if (!draggingSet) {
                    this._beginButtonDragAtPoint(buttonInfo, e.pageX, e.pageY);
                }
            }
        },

        _beginButtonDragAtPoint: function (buttonInfo, x, y) {
            // Initialize starting drag state
            var index = this._buttonInfo.indexOf(buttonInfo);
            var dragInfo = {
                buttonInfo: [buttonInfo],
                startBox: buttonInfo.targetBox,
                startX: x,
                startY: y,
                lastX:  x,
                lastY:  y,
                startIndex: index,
                currentIndex: index,
                connections:  [
                    on(this.ownerDocument, touch.move, lang.hitch(this, "_continueButtonDrag")),
                    on(this.ownerDocument, touch.release, lang.hitch(this, "_endDrag", false))
                ]
            };
            this._buttonDragInfo = dragInfo;
            SelectionBlocker.blockUntilRelease(this.domNode);

            // If the button being dragged belongs to a set then determine limits to keep it from mixing with other sets
            // Note, even if the button doesn't belong to a set, i.e. its setId is undefined it should be restricted
            // to being dragged among other buttons not belonging to a set.
            var setId = this._setIdFromProperties(buttonInfo.properties);
            dragInfo.indexLowerLimit = this._indexOfFirstInSet(setId);
            dragInfo.indexUpperLimit = this._indexOfLastInSet(setId);

            // Set upper and lower drag limits
            var firstIndex = Math.max(dragInfo.indexLowerLimit, this._showingRange ? this._showingRange.low : 0);
            var firstBox = this._buttonInfo[firstIndex].currentBox;
            var lastIndex = Math.min(dragInfo.indexUpperLimit, this._showingRange ? this._showingRange.high : (this._buttonInfo.length - 1));
            var lastBox = this._buttonInfo[lastIndex].currentBox;
            if (this.orientation === "horizontal") {
                dragInfo.lowerLimit = firstBox.l;
                dragInfo.upperLimit = lastBox.l + lastBox.w;
            } else {
                dragInfo.lowerLimit = firstBox.t;
                dragInfo.upperLimit = lastBox.t + lastBox.h;
            }
        },

        _continueButtonDrag: function (e) {
            var dragInfo = this._buttonDragInfo;
            var buttonInfo = dragInfo.buttonInfo[0];
            var isHorizontal = this.orientation === "horizontal";
            var parallelOffset, moveDirection, perpendicularOffset, dragOffDirection;
            var previousIndex, nextIndex, previousInfo, nextInfo, previousBox, nextBox;
            if (isHorizontal) {
                parallelOffset = e.pageX - dragInfo.startX;
                moveDirection = e.pageX - dragInfo.lastX;
                dragInfo.lastX = e.pageX;
                perpendicularOffset = e.pageY - dragInfo.startY;
                dragOffDirection = this.dragOffEdge ? (this.dragOffEdge === "top" ? -1 : 1) : 0;
            } else {
                parallelOffset = e.pageY - dragInfo.startY;
                moveDirection = e.pageY - dragInfo.lastY;
                dragInfo.lastY = e.pageY;
                perpendicularOffset = e.pageX - dragInfo.startX;
                dragOffDirection = this.dragOffEdge ? (this.dragOffEdge === "left" ? -1 : 1) : 0;
            }
            var currentBox = buttonInfo.currentBox;
            var containerBox = domGeometry.position(this.domNode);
            containerBox.l = containerBox.x;
            containerBox.t = containerBox.y;
            // If the mouse has been dragged "far enough" off the bar consider this gesture a drag-off
            // Here "far enough" depends on whether button reordering has already occurred.
            if (this.dragOffEnabled && !Geometry.isNearBox(containerBox, e.pageX, e.pageY,
                                                           dragInfo.hasOrderChanged ? LARGE_DRAG_OFF_THRESHOLD : SMALL_DRAG_OFF_THRESHOLD)
                && (perpendicularOffset * dragOffDirection) >= 0)
            {
                this._endDrag(true);
                this._emitDragOffEvent({
                    button: buttonInfo.button,
                    properties: buttonInfo.properties,
                    index: dragInfo.startIndex - this._indexOfFirstInSet(this._setIdFromProperties(buttonInfo.properties)),
                    mousePoint: {x: e.pageX, y: e.pageY}
                });
                return;
            }

            // Once sufficient movement parallel to the bar occurs prepare the button to be moved.
            if (!dragInfo.hasMoved) {
                if (this._buttonInfo.length > 1 && (parallelOffset > PARALLEL_DRAG_THRESHOLD || parallelOffset < -PARALLEL_DRAG_THRESHOLD)) {
                    dragInfo.hasMoved = true;
                    this._prepareButtonForDrag(buttonInfo);
                } else {
                    return;
                }
            }

            // Reposition the button being dragged, subject to constraints.
            this._dragBox(currentBox, parallelOffset, dragInfo);
            domGeometry.setMarginBox(buttonInfo.button.domNode, currentBox);

            if (dragInfo.isBelowLimit || dragInfo.isAboveLimit) {
                // Scroll a button into view if needed
                if (!this._buttonAnimationTimer) {
                    this._slideButtons();
                }
            } else if (moveDirection !== 0) {
                // Move an adjacent button if needed
                previousIndex = (dragInfo.startIndex < dragInfo.currentIndex) ? dragInfo.currentIndex : (dragInfo.currentIndex - 1);
                nextIndex     = (dragInfo.startIndex > dragInfo.currentIndex) ? dragInfo.currentIndex : (dragInfo.currentIndex + 1);
                if (previousIndex === dragInfo.startIndex) {previousIndex++;}
                if (nextIndex     === dragInfo.startIndex) {nextIndex++;}
                if (isHorizontal) {
                    if (moveDirection < 0 && previousIndex >= dragInfo.indexLowerLimit) {
                        previousInfo = this._buttonInfo[previousIndex];
                        previousBox = previousInfo.targetBox;
                        if (currentBox.l < previousBox.l + previousBox.w / 2) {
                            previousBox.l += currentBox.w;
                            dragInfo.currentIndex--;
                            this._setupAnimation(previousInfo);
                            this._startAnimation();
                            dragInfo.hasOrderChanged = true;
                        }
                    } else if (moveDirection > 0 && nextIndex <= dragInfo.indexUpperLimit) {
                        nextInfo = this._buttonInfo[nextIndex];
                        nextBox = nextInfo.targetBox;
                        if (currentBox.l + currentBox.w > nextBox.l + nextBox.w / 2) {
                            nextBox.l -= currentBox.w;
                            dragInfo.currentIndex++;
                            this._setupAnimation(nextInfo);
                            this._startAnimation();
                            dragInfo.hasOrderChanged = true;
                        }
                    }
                } else {  // is vertical
                    if (moveDirection < 0 && previousIndex >= dragInfo.indexLowerLimit) {
                        previousInfo = this._buttonInfo[previousIndex];
                        previousBox = previousInfo.targetBox;
                        if (currentBox.t < previousBox.t + previousBox.h / 2) {
                            previousBox.t += currentBox.h;
                            dragInfo.currentIndex--;
                            this._setupAnimation(previousInfo);
                            this._startAnimation();
                            dragInfo.hasOrderChanged = true;
                        }
                    } else if (moveDirection > 0 && nextIndex <= dragInfo.indexUpperLimit) {
                        nextInfo = this._buttonInfo[nextIndex];
                        nextBox = nextInfo.targetBox;
                        if (currentBox.t + currentBox.h > nextBox.t + nextBox.h / 2) {
                            nextBox.t -= currentBox.h;
                            dragInfo.currentIndex++;
                            this._setupAnimation(nextInfo);
                            this._startAnimation();
                            dragInfo.hasOrderChanged = true;
                        }
                    }
                }
            }
        },

        // This method exists to allow subclasses to override and tweak the dragOffInfo
        _emitDragOffEvent: function (dragOffInfo) {
            this.emit(LayoutContainerEvent.DRAG_OUT, dragOffInfo);
        },

        _endDrag: function (wasDraggedOff) {
            var that = this;
            var dragInfo = this._buttonDragInfo;
            var buttonPresent = this._buttonInfo[dragInfo.startIndex] === dragInfo.buttonInfo[0];
            var removePending = !dragInfo.buttonInfo[0].removePending;
            delete this._buttonDragInfo;

            // Remove drag listeners
            dragInfo.connections.forEach(function (connection) {
                connection.remove();
            });
            if (dragInfo.hasMoved && buttonPresent  && removePending) {
                // Update the button array if necessary
                if (dragInfo.currentIndex !== dragInfo.startIndex) {
                    this._changeButtonIndices(dragInfo.startIndex, dragInfo.currentIndex, dragInfo.buttonInfo.length);
                    this.emit(LayoutContainerEvent.STATE_CHANGED);
                }

                // Animate dragged button to its final location
                this._computeLayout(dragInfo.currentIndex);
                dragInfo.buttonInfo.forEach(function (buttonInfo) {
                    that._setupAnimation(buttonInfo);
                });
                this._animationDoneResponse = function () {
                    dragInfo.buttonInfo.forEach(function (buttonInfo) {
                        that._restoreButtonAfterDrag(buttonInfo);
                    });
                };
                this._startAnimation();
            }

            if (!this._animationTimer && this._animationDoneResponse) {
                this._animationDoneResponse();
                delete this._animationDoneResponse;
            }
        },

        _beginSetDrag: function (setId, headerBox, e) {
            if (mouse.isLeft(e) && this.reorderingEnabled) {
                this._beginSetDragAtPoint(setId, headerBox, e.pageX, e.pageY);
            }
            LayoutUtils.stopEvent(e);
        },

        _beginSetDragAtPoint: function (setId, headerBox, x, y) {
            // Initialize starting drag state
            var startIndex = this._indexOfFirstInSet(setId);
            var endIndex   = this._indexOfLastInSet(setId);
            var dragInfo = {
                setId: setId,
                headerBox: headerBox,
                startBox: this._getButtonSetBounds(setId),
                startX: x,
                startY: y,
                lastX:  x,
                lastY:  y,
                startIndex: startIndex,
                currentIndex: startIndex,
                connections:  [
                    on(this.ownerDocument, touch.move, lang.hitch(this, "_continueSetDrag")),
                    on(this.ownerDocument, touch.release, lang.hitch(this, "_endDrag", false))
                ]
            };
            this._buttonDragInfo = dragInfo;
            SelectionBlocker.blockUntilRelease(this.domNode);

            var dragCount = endIndex - startIndex + 1;
            var buttonInfo = [];
            var index;
            for (index = dragInfo.startIndex; index < dragInfo.startIndex + dragCount; index++) {
                buttonInfo.push(this._buttonInfo[index]);
            }
            this._buttonDragInfo.buttonInfo = buttonInfo;

            // Set upper and lower drag limits
            dragInfo.indexLowerLimit = 0;
            dragInfo.indexUpperLimit = this._buttonInfo.length - dragCount;

            var firstIndex = this._showingRange ? this._showingRange.low : 0;
            var firstBox = this._buttonInfo[firstIndex].currentBox;
            var lastIndex = Math.max(endIndex, this._showingRange ? this._showingRange.high : (this._buttonInfo.length - 1));
            var lastBox = this._buttonInfo[lastIndex].currentBox;
            if (this.orientation === "horizontal") {
                dragInfo.lowerLimit = firstBox.l;
                dragInfo.upperLimit = lastBox.l + lastBox.w;
            } else {
                dragInfo.lowerLimit = firstBox.t;
                dragInfo.upperLimit = lastBox.t + lastBox.h;
            }
        },

        _continueSetDrag: function (e) {
            var dragInfo = this._buttonDragInfo;
            var isHorizontal = this.orientation === "horizontal";
            var parallelOffset, moveDirection, perpendicularOffset, dragOffDirection;
            var setBox, index, setCount, buttonInfo, count;
            var previousIndex, nextIndex, previousSet, nextSet, previousCount, nextCount, previousInfo, nextInfo, previousBox, nextBox;
            if (isHorizontal) {
                parallelOffset = e.pageX - dragInfo.startX;
                moveDirection = e.pageX - dragInfo.lastX;
                dragInfo.lastX = e.pageX;
                perpendicularOffset = e.pageY - dragInfo.startY;
                dragOffDirection = this.dragOffEdge ? (this.dragOffEdge === "top" ? -1 : 1) : 0;
            } else {
                parallelOffset = e.pageY - dragInfo.startY;
                moveDirection = e.pageY - dragInfo.lastY;
                dragInfo.lastY = e.pageY;
                perpendicularOffset = e.pageX - dragInfo.startX;
                dragOffDirection = this.dragOffEdge ? (this.dragOffEdge === "left" ? -1 : 1) : 0;
            }

            setBox = this._getButtonSetBounds(dragInfo.setId);

            var containerBox = domGeometry.position(this.domNode);
            containerBox.l = containerBox.x;
            containerBox.t = containerBox.y;
            if (dragInfo.headerBox) {
                containerBox = Geometry.unionBox(containerBox, dragInfo.headerBox);
            }
            // TODO: Need to expand to include header box

            // If the mouse has been dragged "far enough" off the bar consider this gesture a drag-off
            // Here "far enough" depends on whether button reordering has already occurred.
            if (this.dragOffEnabled && !Geometry.isNearBox(containerBox, e.pageX, e.pageY,
                dragInfo.hasOrderChanged ? LARGE_DRAG_OFF_THRESHOLD : SMALL_DRAG_OFF_THRESHOLD)
                && (perpendicularOffset * dragOffDirection) >= 0)
            {
                this._endDrag(true);
                var dragOffInfo = {
                    index: dragInfo.startIndex,
                    mousePoint: {x: e.pageX, y: e.pageY}
                };
                if (dragInfo.initiatedButtonDrag) {
                    dragOffInfo.button = dragInfo.buttonInfo[0].button;
                    dragOffInfo.properties = dragInfo.buttonInfo[0].properties;
                    dragOffInfo.index -= this._indexOfFirstInSet(dragInfo.setId);
                } else {
                    dragOffInfo.setId = dragInfo.setId;
                }
                this._emitDragOffEvent(dragOffInfo);
                return;
            }

            // Once sufficient movement parallel to the bar occurs prepare the buttons for actual drag.
            if (!dragInfo.hasMoved) {
                if (parallelOffset > PARALLEL_DRAG_THRESHOLD || parallelOffset < -PARALLEL_DRAG_THRESHOLD) {
                    dragInfo.hasMoved = true;
                    for (index = 0; index < dragInfo.buttonInfo.length; index++) {
                        this._prepareButtonForDrag(dragInfo.buttonInfo[index], true);
                    }
                } else {
                    return;
                }
            }

            // Reposition the buttons being dragged, subject to constraints.
            this._dragBox(setBox, parallelOffset, dragInfo);
            for (index = 0; index < dragInfo.buttonInfo.length; index++) {
                buttonInfo = dragInfo.buttonInfo[index];
                buttonInfo.currentBox = lang.clone(buttonInfo.targetBox);
                if (isHorizontal) {
                    buttonInfo.currentBox.l += setBox.l - dragInfo.startBox.l;
                } else {
                    buttonInfo.currentBox.t += setBox.t - dragInfo.startBox.t;
                }
                domGeometry.setMarginBox(buttonInfo.button.domNode, buttonInfo.currentBox);
            }

            if (dragInfo.isBelowLimit || dragInfo.isAboveLimit) {
                // Scroll a button into view if needed
                if (!this._buttonAnimationTimer) {
                    this._slideButtons();
                }
            } else if (moveDirection !== 0) {
                // Move an adjacent set if needed
                setCount = dragInfo.buttonInfo.length;
                if (dragInfo.currentIndex === dragInfo.startIndex) {
                    previousIndex = dragInfo.currentIndex - 1;
                    nextIndex = dragInfo.currentIndex + setCount;
                } else if (dragInfo.currentIndex > dragInfo.startIndex) {
                    previousIndex = dragInfo.currentIndex + setCount - 1;
                    nextIndex     = previousIndex + 1;
                } else {
                    previousIndex = dragInfo.currentIndex - 1;
                    nextIndex     = previousIndex + 1;
                }
                if (moveDirection < 0 && dragInfo.currentIndex > dragInfo.indexLowerLimit) {
                    previousSet = this._setIdFromProperties(this._buttonInfo[previousIndex].properties);
                    previousCount = previousIndex - this._indexOfFirstInSet(previousSet) + 1;
                    previousBox = this._getButtonSetBounds(previousSet);
                } else if (moveDirection > 0 && dragInfo.currentIndex < dragInfo.indexUpperLimit) {
                    nextSet = this._setIdFromProperties(this._buttonInfo[nextIndex].properties);
                    nextCount = this._indexOfLastInSet(nextSet) - nextIndex + 1;
                    nextBox = this._getButtonSetBounds(nextSet);
                }

                if (isHorizontal) {
                    if (previousCount) {
                        if (setBox.l < previousBox.l + previousBox.w / 2) {
                            dragInfo.currentIndex -= previousCount;
                            for (count = 0; count < previousCount; count++, previousIndex--) {
                                previousInfo = this._buttonInfo[previousIndex];
                                previousBox = previousInfo.targetBox;
                                previousBox.l += setBox.w + this.setGap;
                                this._setupAnimation(previousInfo);
                            }
                            this._startAnimation();
                            dragInfo.hasOrderChanged = true;
                        }
                    } else if (nextCount) {
                        if (setBox.l + setBox.w > nextBox.l + nextBox.w / 2) {
                            dragInfo.currentIndex += nextCount;
                            for (count = 0; count < nextCount; count++, nextIndex++) {
                                nextInfo = this._buttonInfo[nextIndex];
                                nextBox = nextInfo.targetBox;
                                nextBox.l -= setBox.w + this.setGap;
                                this._setupAnimation(nextInfo);
                            }
                            this._startAnimation();
                            dragInfo.hasOrderChanged = true;
                        }
                    }
                } else {  // is vertical
                    if (previousCount) {
                        if (setBox.t < previousBox.t + previousBox.h / 2) {
                            dragInfo.currentIndex -= previousCount;
                            for (count = 0; count < previousCount; count++, previousIndex--) {
                                previousInfo = this._buttonInfo[previousIndex];
                                previousBox = previousInfo.targetBox;
                                previousBox.t += setBox.h + this.setGap;
                                this._setupAnimation(previousInfo);
                            }
                            this._startAnimation();
                            dragInfo.hasOrderChanged = true;
                        }
                    } else if (nextCount) {
                        if (setBox.t + setBox.h > nextBox.t + nextBox.h / 2) {
                            dragInfo.currentIndex += nextCount;
                            for (count = 0; count < nextCount; count++, nextIndex++) {
                                nextInfo = this._buttonInfo[nextIndex];
                                nextBox = nextInfo.targetBox;
                                nextBox.t -= setBox.h + this.setGap;
                                this._setupAnimation(nextInfo);
                            }
                            this._startAnimation();
                            dragInfo.hasOrderChanged = true;
                        }
                    }
                }
            }
        },

        _dragBox: function (box, offset, dragInfo) {
            // Reposition the box being dragged, subject to constraints.
            dragInfo.isAboveLimit = false;
            dragInfo.isBelowLimit = false;
            if (this.orientation === "horizontal") {
                box.l = dragInfo.startBox.l + offset;
                if (box.l <= dragInfo.lowerLimit) {
                    box.l = dragInfo.lowerLimit;
                    dragInfo.isBelowLimit = true;
                } else if (box.l + box.w >= dragInfo.upperLimit) {
                    box.l = dragInfo.upperLimit - box.w;
                    dragInfo.isAboveLimit = true;
                }
            } else {
                box.t = dragInfo.startBox.t + offset;
                if (box.t <= dragInfo.lowerLimit) {
                    box.t = dragInfo.lowerLimit;
                    dragInfo.isBelowLimit = true;
                } else if (box.t + box.h >= dragInfo.upperLimit) {
                    box.t = dragInfo.upperLimit - box.h;
                    dragInfo.isAboveLimit = true;
                }
            }
        },

        _prepareButtonForDrag: function (buttonInfo, isMovingSet) {
            // Move the button above others and disarm it to avoid selection upon release
            if (buttonInfo.button.disarm) {
                buttonInfo.button.disarm();
            }
            if (isMovingSet) {
                domClass.add(buttonInfo.button.domNode, "movingSet");
            } else {
                domClass.add(buttonInfo.button.domNode, "movingButton");
            }
        },

        _restoreButtonAfterDrag: function (buttonInfo) {
            if (buttonInfo.button.rearm) {
                setTimeout(function () {
                    // Button may have been destroyed if dragged out
                    if (buttonInfo.button.domNode) {
                        buttonInfo.button.rearm();
                    }
                }, 0);
            }
            domClass.remove(buttonInfo.button.domNode, "movingButton");
            domClass.remove(buttonInfo.button.domNode, "movingSet");
        },

        _slideButtons: function () {
            var that = this;
            var dragInfo = this._buttonDragInfo;
            var buttonInfo = dragInfo.buttonInfo[0];
            var buttonCount = this._buttonInfo.length;
            var isHorizontal = this.orientation === "horizontal";
            var animate, i, prevIndex, prevInfo, prevWidth, prevHeight, nextIndex, nextInfo, nextWidth, nextHeight, otherInfo;
            if (!this._showingRange) {
                return;
            }
            if (dragInfo.isBelowLimit && dragInfo.currentIndex > 0) {
                // Prepare the previous button to move into view
                prevIndex = dragInfo.currentIndex - 1;
                prevInfo = this._buttonInfo[prevIndex];
                domStyle.set(prevInfo.button.domNode, {visibility : "visible"});
                if (isHorizontal) {
                    prevWidth = prevInfo.currentBox.w;
                    prevInfo.currentBox.l = dragInfo.lowerLimit - prevWidth;
                    prevInfo.targetBox.l = prevInfo.currentBox.l;
                    for (i = prevIndex; i < buttonCount; i++) {
                        otherInfo = this._buttonInfo[i];
                        if (otherInfo !== buttonInfo) {
                            otherInfo.targetBox.l += prevWidth;
                            if (i < dragInfo.currentIndex) {
                                otherInfo.targetBox.l += buttonInfo.currentBox.w;
                            }
                            this._setupAnimation(otherInfo);
                        }
                    }
                } else {
                    prevHeight = prevInfo.currentBox.h;
                    prevInfo.currentBox.t = dragInfo.lowerLimit - prevHeight;
                    prevInfo.targetBox.t = prevInfo.currentBox.t;
                    for (i = prevIndex; i < buttonCount; i++) {
                        otherInfo = this._buttonInfo[i];
                        if (otherInfo !== buttonInfo) {
                            otherInfo.targetBox.t += prevHeight;
                            if (i < dragInfo.currentIndex) {
                                otherInfo.targetBox.t += buttonInfo.currentBox.h;
                            }
                            this._setupAnimation(otherInfo);
                        }
                    }
                }
                dragInfo.currentIndex--;
                this._showingRange.low--;
                this._showingRange.high--;
                animate = true;
            } else if (dragInfo.isAboveLimit && dragInfo.currentIndex < buttonCount - 1) {
                // Prepare the next button to move into view
                nextIndex = dragInfo.currentIndex + 1;
                nextInfo = this._buttonInfo[nextIndex];
                domStyle.set(nextInfo.button.domNode, {visibility : "visible"});
                if (isHorizontal) {
                    nextWidth = nextInfo.currentBox.w;
                    nextInfo.currentBox.l = dragInfo.upperLimit;
                    nextInfo.targetBox.l = nextInfo.currentBox.l;
                    for (i = nextIndex; i >= 0; i--) {
                        otherInfo = this._buttonInfo[i];
                        if (otherInfo !== buttonInfo) {
                            otherInfo.targetBox.l -= nextWidth;
                            if (i > dragInfo.currentIndex) {
                                otherInfo.targetBox.l -= buttonInfo.currentBox.w;
                            }
                            this._setupAnimation(otherInfo);
                        }
                    }
                } else {
                    nextHeight = nextInfo.currentBox.h;
                    nextInfo.currentBox.t = dragInfo.upperLimit;
                    nextInfo.targetBox.t = nextInfo.currentBox.t;
                    for (i = nextIndex; i >= 0; i--) {
                        otherInfo = this._buttonInfo[i];
                        if (otherInfo !== buttonInfo) {
                            otherInfo.targetBox.t -= nextHeight;
                            if (i > dragInfo.currentIndex) {
                                otherInfo.targetBox.t -= buttonInfo.currentBox.h;
                            }
                            this._setupAnimation(otherInfo);
                        }
                    }
                }
                dragInfo.currentIndex++;
                this._showingRange.high++;
                this._showingRange.low++;
                animate = true;
            }

            if (animate) {
                this._animationDoneResponse = function () {
                    if (that._buttonDragInfo === dragInfo && (dragInfo.isBelowLimit || dragInfo.isAboveLimit)) {
                        setTimeout(function() {
                            if (that._buttonDragInfo === dragInfo && (dragInfo.isBelowLimit || dragInfo.isAboveLimit)) {
                                that._slideButtons();
                            }
                        }, SUCCESSIVE_MOVE_DELAY);
                    }
                };
                this._startAnimation();
            }
        },

        _changeButtonIndices: function (fromIndex, toIndex, count) {
            var buttonInfo = this._buttonInfo.splice(fromIndex, count);
            var i;
            for (i = 0; i < buttonInfo.length; i++) {
                this._buttonInfo.splice(toIndex + i, 0, buttonInfo[i]);
            }
            this._updateButtonIndices();
        },

        _updateButtonIndices: function () {
            this._isUpdatingIndices = true;
            var i, properties, setId, previousSetId;
            var setStartIndex = 0;
            for (i = 0; i < this._buttonInfo.length; i++) {
                properties = this._buttonInfo[i].properties;
                setId = this._setIdFromProperties(properties);
                if (previousSetId !== setId) {
                    setStartIndex = i;
                    previousSetId = setId;
                }
                if (properties.isOpen !== false) {
                    properties.set(LayoutChildProperty.INDEX, i - setStartIndex);
                }
            }
            this._isUpdatingIndices = false;
        },

        on: function(/*String|Function*/ type /*, func Function */){
            // Enable drag off if a callback is attached to the relevant event
            if (type === LayoutContainerEvent.DRAG_OUT) {
                this.dragOffEnabled = true;
            }
            return this.inherited(arguments);
        }
    });

    ButtonBar.ORIENTATION = ORIENTATION;
    ButtonBar.SHRINK_WIDTH_TO_FIT = SHRINK_WIDTH_TO_FIT;
    ButtonBar.SHRINK_HEIGHT_TO_FIT = SHRINK_HEIGHT_TO_FIT;
    ButtonBar.MINIMUM_BUTTON_WIDTH = MINIMUM_BUTTON_WIDTH;
    ButtonBar.MINIMUM_BUTTON_HEIGHT = MINIMUM_BUTTON_HEIGHT;

    return ButtonBar;
});
